/**
 * authenticator provides functions to login or create an account
 */

import jsSHA from 'jssha'
import { get } from 'lodash'
import moment from 'moment'
import axios from 'axios'
import { me } from './GraphQlClientService'
import { storageMode, trackSignedUp } from '../helpers'
import { formatAndRethrowError } from './GraphQlClientService'

const _localStorage = storageMode('localStorage')
const _sessionStorage = storageMode('sessionStorage')

const CLIENT_ID = process.env.NEXT_PUBLIC_CLIENT_ID
const CLIENT_SECRET = process.env.NEXT_PUBLIC_CLIENT_SECRET
const STARBASE_URL = process.env.NEXT_PUBLIC_STARBASE_URL

/**
 * sign into an account and set access token in local storage
 *
 * @param {*} email
 * @param {*} password
 * @returns
 */
export const signIn = async (email, password, rememberAuth) => {
  if (email & password) {
    throw new Error('Enter your email and password.')
  }

  const res = await makeSignedAPICall('POST', '/api/v1/auth/token', {
    username: email.trim().toLowerCase(),
    password: password,
    grant_type: 'password'
  })

  const { access_token: accessToken } = res

  if (rememberAuth) {
    _localStorage.setItem('accessToken', accessToken)
    _localStorage.setItem('lastUpdated', moment().unix())
  }

  _sessionStorage.setItem('accessToken', accessToken)
  _sessionStorage.setItem('lastUpdated', moment().unix())

  return me()
}


/**
 * create an account
 *
 * @param {*} email
 * @param {*} nameFirst
 * @param {*} nameLast
 * @param {*} phone
 * @param {*} password
 * @param {string} referredByCode
 */
export const signUp = async (
  email,
  nameFirst,
  nameLast,
  phone,
  referredByCode,
  oneTimePasscode,
  password
) => {
  signOut()
  return makeSignedAPICall('POST', '/api/v1/users/', {
    email,
    nameFirst,
    nameLast,
    phone,
    referredByCode,
    oneTimePasscode,
    password
  })
    .then(res => {
      trackSignedUp({
        email,
        first_name: nameFirst,
        last_name: nameLast,
        phone,
        username: email,
      }, res.data.id)

      if (oneTimePasscode && !password) {
        return exchangeOTPForTokens(oneTimePasscode, phone)
          .then((tokens) => {
            // find the token that was just created
            const newToken = tokens.find((token) => token.email === email.toLowerCase())
            return loginWithToken(newToken.access_token)
          })
      }

      return signIn(email, password)
    })
}

export const passwordlessStart = async (phone) => {
  return makeSignedAPICall('POST', '/api/v1/auth/passwordless/start', { phone })
}

/**
 * sends password reset email to given email
 * given POST method will send email to user
 * given PUT will update user with body
 *
 * @param {object} body
 * * @param {string} method
 */
export const resetPasswordEmail = async (method, body) => {
  try {
    await makeSignedAPICall(method, '/api/v1/users/password-reset', body)
  } catch (e) {
    console.error(e)
  }
}

export const resetPassword = async (password, token, isProvider) => {
  try {
    const body = { password }
    // provider endpoint is expecting there to be a key in the body instead of a token
    if (isProvider) {
      body.key = token
    } else {
      body.token = token
    }

    await makeSignedAPICall('PUT', '/api/v1/users/password-reset', body)
  } catch (error) {    
    console.log(error.response.data)
    formatAndRethrowError(error.response.data)
  }
}

/**
 * fetches a user by their referralCode
 *
 * @param {string} code
 */
export const findByReferralCode = async code =>
  await makeSignedAPICall('GET', `/api/v1/referrals/${code}/sender`)

/**
 * adds client signature header and makes request
 *
 * @param {*} method
 * @param {*} endpoint
 * @param {*} body
 * @returns
 */
export const makeSignedAPICall = async (method, endpoint, body) => {
  const sha = new jsSHA('SHA-256', 'TEXT')
  sha.setHMACKey(CLIENT_SECRET, 'TEXT')
  sha.update([method, endpoint, JSON.stringify(body)].join('|'))
  const hmac = sha.getHMAC('B64')

  const url = STARBASE_URL + endpoint

  try {
    const res = await axios({
      method,
      url,
      headers: {
        Authorization: 'Glam ' + [CLIENT_ID, hmac].join(':'),
        'Content-Type': 'application/json'
      },

      data: body,
      timeout: 15000,
      responseType: 'json'
    })

    if (res.status > 399) {
      const err = new Error(
        get(res, 'data.errors.0.message') || 'Error making request.'
      )
      err.res = res
      err.body = res.data

      throw err
    }

    return res.data
  } catch (error) {
    console.warn('makeSignedAPICall', error)
    throw error
  }
}

export const signOut = () => {
  _localStorage.removeItem('accessToken')
  _localStorage.removeItem('lastUpdated')
  _localStorage.removeItem('userInfo')
  _localStorage.removeItem('book')
  _sessionStorage.removeItem('accessToken')
  _sessionStorage.removeItem('book')

  return null
}
export const isAuthenticated = () =>
  !!_localStorage.getItem('accessToken') ||
  !!_sessionStorage.getItem('accessToken')

/* exchangeCodeForToken
 * called to logic from a magic link from marketing
 * different from exchangeOTPCode
 */
export const exchangeCodeForToken = async (code) => {
  console.log('exchanging...', code)
  try {
    const { access_token } = await makeSignedAPICall('POST', '/api/v1/auth/token', {
      grant_type: 'authorization_code',
      code: code.replace(/\s/g, '+')
    })

    saveAccessToken(access_token)
    return me()
  } catch (e) {
    console.error('exchangeCodeForToken', e)
    throw e
  }
}

/* exchangeOTPCode
 * returns all access tokens associated to a phone number for account selection
 */
export const exchangeOTPForTokens = async (code, phone) => {
  try {
    const tokens = await makeSignedAPICall('POST', '/api/v1/auth/token', {
      code,
      phone,
      grant_type: 'OTP_code'
    })

    return tokens
  } catch (e) {
    console.error('exchangeOTPCode', e)
    throw e
  }
}

export const saveAccessToken = (access_token, rememberAuth = true) => {
  if (rememberAuth) {
    _localStorage.setItem('accessToken', access_token)
    _localStorage.setItem('lastUpdate', moment().unix())
  } else {
    _sessionStorage.setItem('accessToken', access_token)
    _sessionStorage.setItem('lastUpdated', moment().unix())
  }
}

export const loginWithToken = async (token) => {
  try {
    saveAccessToken(token)
    return me()
  } catch (e) {
    console.error('loginWithToken', e)
    throw e
  }
}

// sets the access token to the agent parameter and returns the standard me
// used when linking from starbase appointment detail to client survey
// allows agents to answer surveys for a client
export const authenticateAgent = (agent) => {
  try {
    _sessionStorage.setItem('accessToken', agent)
    return me()
  } catch (error) {
    console.error('authenticateAgent', error)
    throw error
  }
}

export const getAccessToken = () => {
  return (
    _localStorage.getItem('accessToken') ||
    _sessionStorage.getItem('accessToken')
  )
}

export const fetchIntercomJWT = async (userId) => {
  // access token if we have on
  const accessToken = getAccessToken()

  if (!accessToken) {
    return
  }

  const res = await fetch(
    STARBASE_URL.concat(`/api/v1/users/${userId}/intercom-jwt`),
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer `.concat(accessToken),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({}),
      timeout: 20000,
      responseType: 'json'
    }
  )

  if (res.status > 299) {
    console.error('Error fetching Intercom JWT')
    return null
  }

  const { data: { token }} = await res.json()

  return token
};
